import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Loader2, MapPin, Building2, Layers, Tag, CheckCheck, RefreshCw, MailWarning, Boxes } from "lucide-react";
import { toast } from "sonner";

/** Panel admin — normaliza y deduplica provincias, ciudades, cadenas y marcas. */
export default function AdminNormalizacion() {
  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-100">Normalización y deduplicación</h1>
        <p className="text-sm text-zinc-400">
          Detecta variantes duplicadas (mayúsculas, tildes, espacios) y las fusiona a un valor canónico.
        </p>
      </header>

      <Tabs defaultValue="provincias" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="provincias" data-testid="norm-tab-provincias">
            <MapPin className="w-4 h-4 mr-2" /> Provincias
          </TabsTrigger>
          <TabsTrigger value="ciudades" data-testid="norm-tab-ciudades">
            <Building2 className="w-4 h-4 mr-2" /> Ciudades
          </TabsTrigger>
          <TabsTrigger value="cadenas" data-testid="norm-tab-cadenas">
            <Layers className="w-4 h-4 mr-2" /> Cadenas
          </TabsTrigger>
          <TabsTrigger value="marcas" data-testid="norm-tab-marcas">
            <Tag className="w-4 h-4 mr-2" /> Marcas
          </TabsTrigger>
          <TabsTrigger value="limbo" data-testid="norm-tab-limbo">
            <MailWarning className="w-4 h-4 mr-2" /> Peticiones en limbo (IA)
          </TabsTrigger>
          <TabsTrigger value="componentes" data-testid="norm-tab-componentes">
            <Boxes className="w-4 h-4 mr-2" /> Componentes legacy
          </TabsTrigger>
        </TabsList>

        <TabsContent value="provincias"><PanelProvincias /></TabsContent>
        <TabsContent value="ciudades"><PanelCiudades /></TabsContent>
        <TabsContent value="cadenas"><PanelCadenas /></TabsContent>
        <TabsContent value="marcas"><PanelMarcas /></TabsContent>
        <TabsContent value="limbo"><PanelLimbo /></TabsContent>
        <TabsContent value="componentes"><PanelComponentesLegacy /></TabsContent>
      </Tabs>
    </div>
  );
}

function PanelProvincias() {
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const aplicar = async () => {
    if (!confirm("Se recorrerán todos los espacios y se canonizará el campo `provincia`. Continuar?")) return;
    setApplying(true);
    try {
      const { data } = await api.post("/normalizacion/aplicar-provincias");
      setResult(data);
      toast.success(`Provincias canonizadas: ${data.cambios}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setApplying(false); }
  };
  return (
    <Card className="p-4 bg-zinc-900/40 border-zinc-800 mt-4">
      <h2 className="text-lg font-semibold text-zinc-100 mb-2">Provincias canónicas</h2>
      <p className="text-sm text-zinc-400 mb-3">
        Fuente canónica: 52 provincias de España + 20 distritos/regiones de Portugal.
        Alias reconocidos: "Coruña" → "A Coruña", "Vizcaya" → "Bizkaia", "Guipúzcoa" → "Gipuzkoa", "Gerona" → "Girona", "Lérida" → "Lleida", "Orense" → "Ourense", "Lisbon" → "Lisboa", "Oporto" → "Porto".
      </p>
      <Button onClick={aplicar} disabled={applying}
        className="bg-amber-500 hover:bg-amber-400 text-zinc-900" data-testid="btn-norm-provincias">
        {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</> : <><CheckCheck className="w-4 h-4 mr-2" /> Aplicar normalización a todos los espacios</>}
      </Button>
      {result && (
        <div className="mt-4 text-xs">
          <div className="text-emerald-300">✅ {result.cambios} espacios actualizados.</div>
          {result.detalle?.length > 0 && (
            <div className="mt-2 max-h-64 overflow-auto bg-zinc-950 border border-zinc-800 rounded p-2">
              {result.detalle.map((c, i) => (
                <div key={i} className="text-zinc-300 font-mono">
                  {c.nombre}: <span className="text-red-300">{c.de}</span> → <span className="text-emerald-300">{c.a}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function DedupPanel({ endpoint, mergeEndpoint, labelSingular, labelPlural, testIdPrefix, isChain = false }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setData(data); }
    catch (e) { toast.error("Error cargando"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const mergeGrupo = async (grupo) => {
    const msg = isChain
      ? `Fusionar ${grupo.variantes.length} cadena(s) en "${grupo.canonical.nombre}"?`
      : `Fusionar ${grupo.variantes.length} variantes en "${grupo.canonical}"?`;
    if (!confirm(msg)) return;
    setBusy(grupo.norm || grupo.canonical);
    try {
      const payload = isChain
        ? { canonical_id: grupo.canonical.id, variante_ids: grupo.variantes.map((v) => v.id) }
        : { canonical: grupo.canonical, variantes: grupo.variantes };
      const { data: r } = await api.post(mergeEndpoint, payload);
      toast.success(`Actualizados: ${JSON.stringify(r).slice(0, 100)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="p-6 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando…</div>;
  if (!data) return null;
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          <b className="text-zinc-100">{data.canonicas?.length || 0}</b> {labelPlural} canónicas ·{" "}
          <b className={data.duplicados?.length ? "text-amber-300" : "text-emerald-300"}>
            {data.duplicados?.length || 0}
          </b> grupo(s) con duplicados
        </div>
        <Button onClick={load} size="sm" variant="ghost" className="text-zinc-400" data-testid={`${testIdPrefix}-reload`}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recargar
        </Button>
      </div>
      {data.duplicados?.length === 0 ? (
        <div className="p-4 bg-emerald-500/5 border border-emerald-500/30 rounded text-sm text-emerald-300">
          ✅ Sin duplicados detectados en {labelPlural}.
        </div>
      ) : (
        <div className="space-y-2">
          {data.duplicados.map((g, i) => (
            <div key={i} className="p-3 bg-zinc-950/60 border border-zinc-800 rounded"
                 data-testid={`${testIdPrefix}-grupo-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-emerald-300 font-semibold text-sm">
                  {isChain ? g.canonical.nombre : g.canonical}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">canonical</span>
                <Button onClick={() => mergeGrupo(g)} size="sm" className="ml-auto bg-amber-500 hover:bg-amber-400 text-zinc-900 h-7"
                  disabled={busy === (g.norm || g.canonical)}
                  data-testid={`${testIdPrefix}-merge-${i}`}>
                  {busy === (g.norm || g.canonical) ? <Loader2 className="w-3 h-3 animate-spin" /> : "Fusionar"}
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(isChain ? g.variantes.map((v) => v.nombre) : g.variantes).map((v, j) => (
                  <span key={j} className="text-xs bg-red-500/10 border border-red-500/30 text-red-200 px-2 py-0.5 rounded font-mono">
                    {v}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PanelCiudades() {
  return <DedupPanel endpoint="/normalizacion/ciudades" mergeEndpoint="/normalizacion/ciudades/merge"
    labelSingular="ciudad" labelPlural="ciudades" testIdPrefix="norm-ciudades" />;
}
function PanelCadenas() {
  return <DedupPanel endpoint="/normalizacion/cadenas" mergeEndpoint="/normalizacion/cadenas/merge"
    labelSingular="cadena" labelPlural="cadenas" testIdPrefix="norm-cadenas" isChain />;
}
function PanelMarcas() {
  return <DedupPanel endpoint="/normalizacion/marcas" mergeEndpoint="/normalizacion/marcas/merge"
    labelSingular="marca" labelPlural="marcas" testIdPrefix="norm-marcas" />;
}

// ==================== PANEL LIMBO (peticiones IA con estado inconsistente) ====================
function PanelLimbo() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const cargarPreview = async ({ keepResult = false } = {}) => {
    setLoading(true);
    if (!keepResult) setResult(null);
    try {
      const { data } = await api.get("/peticiones-ia/fix-limbo/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarPreview(); }, []); // eslint-disable-line

  const aplicar = async (dryRun) => {
    if (!dryRun && !confirm(
      `Se arreglarán ${preview?.peticiones_en_limbo || 0} peticiones creadas desde emails (Bandeja IA).\n\n` +
      `• Estado "borrador" → "aprobada" (para las que tienen cliente + espacios completos)\n` +
      `• Reasignación de tipo (montaje → solo_montaje cuando no hay componentes)\n` +
      `• Las que falten datos se marcarán "revision_manual" para que las revises\n\n¿Continuar?`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post("/peticiones-ia/fix-limbo", { dry_run: dryRun });
      setResult(data);
      if (!dryRun) {
        toast.success(
          `Arregladas ${data.peticiones_arregladas} peticiones + ${data.lineas_arregladas} líneas. ` +
          `${data.peticiones_marcadas_revision_manual} marcadas para revisión manual.`
        );
        cargarPreview({ keepResult: true });
      } else {
        toast.info(`Simulación: se arreglarían ${data.peticiones_arregladas} peticiones.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error aplicando fix");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="p-4 bg-zinc-900/40 border-zinc-800 mt-4" data-testid="panel-limbo">
      <h2 className="text-lg font-semibold text-zinc-100 mb-2 flex items-center gap-2">
        <MailWarning className="w-5 h-5 text-amber-400" /> Peticiones en limbo (creadas desde email)
      </h2>
      <p className="text-sm text-zinc-400 mb-3">
        Peticiones aprobadas desde la <b className="text-zinc-200">Bandeja IA</b> que quedaron en estado
        <code className="mx-1 px-1 bg-zinc-800 text-amber-300 rounded">borrador</code>
        y por eso no aparecen en la asignación a rutas. Este panel las arregla: promociona el estado a
        <code className="mx-1 px-1 bg-zinc-800 text-emerald-300 rounded">aprobada</code>
        y reasigna el tipo LEGACY de las líneas (<code>montaje</code> → <code>solo_montaje</code>)
        cuando no hay componentes asociados.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <Button onClick={cargarPreview} size="sm" variant="ghost" className="text-zinc-400"
          data-testid="btn-limbo-reload">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recargar
        </Button>
        <Button onClick={() => aplicar(true)} disabled={loading || applying || !preview?.peticiones_en_limbo}
          size="sm" variant="outline" className="border-zinc-700 text-zinc-200"
          data-testid="btn-limbo-dry-run">
          Simular (dry-run)
        </Button>
        <Button onClick={() => aplicar(false)} disabled={loading || applying || !preview?.peticiones_en_limbo}
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900"
          data-testid="btn-limbo-apply">
          {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</> :
            <><CheckCheck className="w-4 h-4 mr-2" /> Aplicar fix a todas ({preview?.peticiones_en_limbo || 0})</>}
        </Button>
      </div>

      {loading && (
        <div className="text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Analizando…
        </div>
      )}

      {!loading && preview && preview.peticiones_en_limbo === 0 && (
        <div className="p-3 bg-emerald-500/5 border border-emerald-500/30 rounded text-sm text-emerald-300"
             data-testid="limbo-empty">
          ✅ No hay peticiones en limbo. Todo está en orden.
        </div>
      )}

      {!loading && preview && preview.peticiones_en_limbo > 0 && (
        <div className="space-y-2 max-h-96 overflow-auto" data-testid="limbo-list">
          <div className="text-xs text-zinc-400 mb-2">
            Detectados <b className="text-amber-300">{preview.peticiones_en_limbo}</b> de{" "}
            {preview.total_borradores_aprobados} borradores aprobados totales.
          </div>
          {preview.afectadas.map((a, i) => (
            <div key={i} className="p-3 bg-zinc-950/60 border border-zinc-800 rounded"
                 data-testid={`limbo-item-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-100 font-semibold">
                  {a.peticion.codigo || a.peticion.id.slice(0, 8)} · {a.peticion.nombre}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 uppercase">
                  {a.peticion.estado}
                </span>
                {a.sin_espacio > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/30">
                    {a.sin_espacio} línea(s) sin espacio → revisión manual
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                <span>Email: <b className="text-zinc-400">{a.email_subject || "—"}</b></span>
                <span>Cliente IA: <b className="text-zinc-400">{a.cliente_nombre_sugerido || "—"}</b></span>
                <span>{a.total_lineas} línea(s), {a.lineas_a_fix.length} a corregir tipo</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="mt-4 p-3 bg-emerald-500/5 border border-emerald-500/30 rounded text-xs text-emerald-200"
             data-testid="limbo-result">
          <div className="font-semibold text-emerald-300 mb-1">
            {result.dry_run ? "Simulación completada" : "Fix aplicado"}
          </div>
          <div>Peticiones promocionadas a &quot;aprobada&quot;: <b>{result.peticiones_arregladas}</b></div>
          <div>Líneas con tipo reasignado: <b>{result.lineas_arregladas}</b></div>
          <div>Peticiones marcadas para revisión manual: <b>{result.peticiones_marcadas_revision_manual}</b></div>
        </div>
      )}

      <PanelFixTiposLegacy />
    </Card>
  );
}

// ==================== FIX TIPOS LEGACY (amplio, no solo Bandeja IA) ====================
function PanelFixTiposLegacy() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/peticiones-ia/fix-tipos-legacy/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando preview tipos LEGACY");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const aplicar = async (dryRun) => {
    if (!dryRun && !confirm(
      `Se reasignará el tipo LEGACY de ${preview?.total_lineas_a_arreglar || 0} líneas (en ` +
      `${preview?.total_peticiones_afectadas || 0} peticiones) para que aparezcan en el planificador de rutas.\n\n` +
      `Ejemplo típico: tipos=["produccion","montaje"] pero tipo LEGACY guardado como "produccion" ` +
      `(el planificador filtra por LEGACY y las excluía).\n\n¿Continuar?`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post("/peticiones-ia/fix-tipos-legacy", { dry_run: dryRun });
      setResult(data);
      if (!dryRun) {
        toast.success(`Reasignadas ${data.lineas_arregladas} líneas en ${data.peticiones_tocadas} peticiones.`);
        cargar();
      } else {
        toast.info(`Simulación: se reasignarían ${data.lineas_arregladas} líneas.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error aplicando fix tipos LEGACY");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="mt-8 pt-4 border-t border-zinc-800" data-testid="panel-fix-tipos-legacy">
      <h3 className="text-base font-semibold text-zinc-100 mb-1 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-amber-400" /> Reparar tipo LEGACY de líneas (amplio)
      </h3>
      <p className="text-xs text-zinc-400 mb-3">
        Busca líneas de peticiones YA aprobadas donde <code className="px-1 bg-zinc-800 text-amber-300 rounded">tipos[]</code>
        multi está bien rellenado pero <code className="px-1 bg-zinc-800 text-amber-300 rounded">tipo</code> LEGACY quedó mal
        (típico: <code>produccion</code> en vez de <code>produccion_montaje</code>). El planificador de rutas filtra por LEGACY,
        así que esas líneas nunca aparecían para rutar.
      </p>

      <div className="flex items-center gap-2 mb-3">
        <Button onClick={cargar} size="sm" variant="ghost" className="text-zinc-400"
          data-testid="btn-fix-tipos-reload">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recargar
        </Button>
        <Button onClick={() => aplicar(true)} disabled={loading || applying || !preview?.total_lineas_a_arreglar}
          size="sm" variant="outline" className="border-zinc-700 text-zinc-200"
          data-testid="btn-fix-tipos-dry-run">
          Simular
        </Button>
        <Button onClick={() => aplicar(false)} disabled={loading || applying || !preview?.total_lineas_a_arreglar}
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900"
          data-testid="btn-fix-tipos-apply">
          {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</> :
            <><CheckCheck className="w-4 h-4 mr-2" /> Reparar {preview?.total_lineas_a_arreglar || 0} líneas</>}
        </Button>
      </div>

      {loading && (
        <div className="text-zinc-500 text-xs">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Analizando líneas…
        </div>
      )}

      {!loading && preview && preview.total_lineas_a_arreglar === 0 && (
        <div className="p-3 bg-emerald-500/5 border border-emerald-500/30 rounded text-xs text-emerald-300"
             data-testid="fix-tipos-empty">
          ✅ No hay líneas con tipo LEGACY inconsistente.
        </div>
      )}

      {!loading && preview && preview.total_lineas_a_arreglar > 0 && (
        <div className="space-y-2 max-h-96 overflow-auto" data-testid="fix-tipos-list">
          <div className="text-xs text-zinc-400 mb-2">
            Detectadas <b className="text-amber-300">{preview.total_lineas_a_arreglar}</b> líneas en{" "}
            <b className="text-amber-300">{preview.total_peticiones_afectadas}</b> peticiones.
          </div>
          {preview.por_peticion.map((g, i) => (
            <div key={i} className="p-2 bg-zinc-950/60 border border-zinc-800 rounded"
                 data-testid={`fix-tipos-peticion-${i}`}>
              <div className="text-sm text-zinc-100 font-semibold mb-1">
                {g.peticion.codigo || g.peticion.id?.slice(0, 8)} · {g.peticion.nombre}
                <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                  {g.peticion.estado}
                </span>
              </div>
              {g.lineas.map((ln, j) => (
                <div key={j} className="text-xs text-zinc-400 pl-3 border-l border-zinc-800 mt-1">
                  <div>
                    <span className="text-red-300 font-mono">{ln.old_tipo || "(vacío)"}</span>{" → "}
                    <span className="text-emerald-300 font-mono">{ln.new_tipo}</span>
                    <span className="text-zinc-500 ml-2">tipos=[{(ln.tipos_multi || []).join(", ")}]</span>
                    <span className="text-zinc-500 ml-2">comp={ln.n_componentes}</span>
                  </div>
                  <div className="text-zinc-500 italic truncate">{ln.descripcion}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/30 rounded text-xs text-emerald-200"
             data-testid="fix-tipos-result">
          <div className="font-semibold text-emerald-300 mb-1">
            {result.dry_run ? "Simulación completada" : "Fix aplicado"}
          </div>
          <div>Líneas reasignadas: <b>{result.lineas_arregladas}</b></div>
          <div>Peticiones tocadas: <b>{result.peticiones_tocadas}</b></div>
        </div>
      )}
    </div>
  );
}

// Jul-2026: informe del limbo legacy de componentes (migración auto al arrancar)
function PanelComponentesLegacy() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/normalizacion/componentes-legacy");
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  return (
    <Card className="p-4 bg-zinc-900/40 border-zinc-800 mt-4" data-testid="panel-componentes-legacy">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-zinc-100">Componentes legacy (limbo)</h2>
        <Button variant="outline" size="sm" onClick={cargar} disabled={loading}
          className="border-zinc-700 text-zinc-300" data-testid="btn-refrescar-comp-legacy">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-sm text-zinc-400 mb-3">
        La normalización se ejecuta <span className="text-emerald-300 font-semibold">automáticamente en cada arranque del backend</span>:
        estado "pendiente" (limbo) → estado inicial según origen; orígenes muertos (proveedor, externo, proveedor_a_cliente) → Flujo v2.
        Sin esto, los componentes no aparecen en Materiales de montaje, el Kanban ni el planificador de rutas.
      </p>
      {loading && !data ? (
        <div className="text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cargando…</div>
      ) : data && (
        <>
          <div className={`text-sm font-semibold mb-2 ${data.limbo_total === 0 ? "text-emerald-300" : "text-amber-300"}`}
            data-testid="comp-legacy-total">
            {data.limbo_total === 0
              ? "✅ Sin componentes en limbo — todo el flujo está normalizado."
              : `⚠️ ${data.limbo_total} componentes en limbo (se normalizarán en el próximo arranque/redeploy).`}
          </div>
          {data.limbo_actual?.length > 0 && (
            <div className="mb-3 max-h-40 overflow-auto bg-zinc-950 border border-zinc-800 rounded p-2 text-xs font-mono">
              {data.limbo_actual.map((x, i) => (
                <div key={i} className="text-zinc-300">
                  origen <span className="text-red-300">{x.origen}</span> · estado <span className="text-red-300">{x.estado}</span> → {x.n}
                </div>
              ))}
            </div>
          )}
          <h3 className="text-sm font-semibold text-zinc-200 mb-1">Historial de normalizaciones</h3>
          {(data.historial || []).length === 0 ? (
            <div className="text-xs text-zinc-500">Sin normalizaciones registradas todavía.</div>
          ) : (
            <div className="max-h-64 overflow-auto space-y-2">
              {data.historial.map((h, i) => (
                <div key={i} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs">
                  <div className="text-zinc-400 mb-1">🕒 {(h.at || "").replace("T", " ").slice(0, 16)} · <span className="text-emerald-300">{h.resultado?.total} componentes</span></div>
                  {(h.resultado?.origenes || []).map((o, j) => (
                    <div key={`o${j}`} className="text-zinc-300 font-mono">origen: <span className="text-red-300">{o.de}</span> → <span className="text-emerald-300">{o.a}</span> ({o.n})</div>
                  ))}
                  {(h.resultado?.estados || []).map((e, j) => (
                    <div key={`e${j}`} className="text-zinc-300 font-mono">estado limbo ({e.origen}) → <span className="text-emerald-300">{e.nuevo_estado}</span> ({e.n})</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
